import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Trash2, Loader2, Search, X, Eye, Mail, MailOpen, User, Reply, Calendar } from 'lucide-react'
import { useState } from 'react'

const AdminMessages = () => {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewingMessage, setViewingMessage] = useState(null)
  const [messageToDelete, setMessageToDelete] = useState(null)

  const { data: messages, isLoading } = useQuery({
    queryKey: ['admin-messages'],
    queryFn: async () => {
      const res = await api.get('/contact/messages')
      return res.data
    }
  })

  const markAsReadMutation = useMutation({
    mutationFn: (id) => api.put(`/contact/messages/${id}/lu`),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-messages'])
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/contact/messages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-messages'])
      setMessageToDelete(null)
    }
  })

  const handleView = (message) => {
    setViewingMessage(message)
    setIsViewModalOpen(true)
    if (!message.lu) {
      markAsReadMutation.mutate(message.id)
    }
  }

  const filteredMessages = messages?.filter(msg => {
    const search = searchTerm.toLowerCase()
    return (
      msg.nom?.toLowerCase().includes(search) ||
      msg.prenom?.toLowerCase().includes(search) ||
      msg.email?.toLowerCase().includes(search) ||
      msg.objet?.toLowerCase().includes(search) ||
      msg.message?.toLowerCase().includes(search)
    )
  }).sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at) : 0
    const dateB = b.created_at ? new Date(b.created_at) : 0
    return dateB - dateA
  })

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cidh-gold" size={48} /></div>

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-cidh-blue font-montserrat">Gestion des Messages</h3>
          <p className="text-gray-500">Consultez et gérez les messages reçus via le formulaire de contact</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher un message, un nom ou un email..." 
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
              <tr>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4">Expéditeur</th>
                <th className="px-6 py-4">Objet</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMessages?.map((msg) => (
                <tr key={msg.id} className={`hover:bg-gray-50/50 transition-colors group ${!msg.lu ? 'bg-cidh-gold/5' : ''}`}>
                  <td className="px-6 py-4">
                    {!msg.lu ? (
                      <span className="flex items-center gap-1 text-cidh-gold font-bold text-[10px] uppercase">
                        <Mail size={14} /> Nouveau
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400 text-[10px] uppercase">
                        <MailOpen size={14} /> Lu
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-bold text-cidh-blue text-sm">{msg.prenom ? `${msg.prenom} ${msg.nom}` : msg.nom}</span>
                      <span className="text-xs text-gray-500">{msg.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-700 line-clamp-1">{msg.objet}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-500">
                      {msg.created_at ? new Date(msg.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleView(msg)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Lire le message">
                        <Eye size={18} />
                      </button>
                      <button 
                        onClick={() => setMessageToDelete(msg)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredMessages?.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center text-gray-400 italic">
                    Aucun message trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Message Modal */}
      {isViewModalOpen && viewingMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl animate-scale-in overflow-hidden text-cidh-blue">
            <div className="p-6 border-b flex justify-between items-center bg-white sticky top-0 z-10">
              <h4 className="text-xl font-bold font-montserrat flex items-center gap-2">
                <Mail className="text-cidh-gold" size={24} /> Message de Contact
              </h4>
              <button onClick={() => { setIsViewModalOpen(false); setViewingMessage(null); }} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} /></button>
            </div>
            
            <div className="flex-grow overflow-y-auto p-8">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 flex items-center gap-1">
                      <User size={12} /> Expéditeur
                    </p>
                    <p className="font-bold">{viewingMessage.prenom ? `${viewingMessage.prenom} ${viewingMessage.nom}` : viewingMessage.nom}</p>
                    <p className="text-sm text-gray-500">{viewingMessage.email}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 flex items-center gap-1">
                      <Calendar size={12} /> Reçu le
                    </p>
                    <p className="font-bold">
                      {(() => {
                        try {
                          return viewingMessage.created_at 
                            ? new Date(viewingMessage.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) 
                            : 'N/A'
                        } catch (e) {
                          return 'Date invalide'
                        }
                      })()}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 flex items-center gap-1">
                    <Reply size={12} /> Objet
                  </p>
                  <p className="text-lg font-bold text-cidh-blue">{viewingMessage.objet}</p>
                </div>

                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Message</p>
                  <div className="bg-gray-50 p-6 rounded-2xl text-gray-700 leading-relaxed whitespace-pre-line border border-gray-100 min-h-[150px]">
                    {viewingMessage.message}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3 bg-white sticky bottom-0 z-10">
              <button 
                onClick={() => {
                  window.location.href = `mailto:${viewingMessage.email}?subject=Re: ${viewingMessage.objet}`;
                }}
                className="px-6 py-3 bg-cidh-gold text-white rounded-xl font-bold hover:bg-cidh-gold/90 transition-all shadow-lg active:scale-95 flex items-center gap-2"
              >
                <Reply size={18} /> Répondre par Email
              </button>
              <button 
                onClick={() => { setIsViewModalOpen(false); setViewingMessage(null); }} 
                className="px-6 py-3 bg-cidh-blue text-white rounded-xl font-bold hover:bg-cidh-blue/90 transition-all shadow-lg active:scale-95"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {messageToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl animate-scale-in text-center">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trash2 size={40} />
            </div>
            <h4 className="text-2xl font-bold text-cidh-blue mb-2 font-montserrat">Supprimer ce message ?</h4>
            <p className="text-gray-500 mb-8">
              Êtes-vous sûr de vouloir supprimer ce message de <strong>{messageToDelete.nom}</strong> ? Cette action est irréversible.
            </p>
            <div className="flex gap-4">
              <button 
                onClick={() => setMessageToDelete(null)}
                className="flex-grow py-3 border border-gray-200 rounded-xl font-bold text-gray-400 hover:bg-gray-50 transition-all"
              >
                Annuler
              </button>
              <button 
                onClick={() => deleteMutation.mutate(messageToDelete.id)}
                disabled={deleteMutation.isPending}
                className="flex-grow py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-all shadow-lg shadow-red-500/20 active:scale-95 disabled:opacity-50"
              >
                {deleteMutation.isPending ? <Loader2 className="animate-spin mx-auto" size={20} /> : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminMessages
